import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Sparkles, Trophy, BookOpen, ScrollText, Circle, TrendingUp } from "lucide-react";

/**
 * Bottom sheet exposing header actions that are hidden on mobile because of
 * space. Each row is a chunky brutalist button so it stays consistent with
 * the rest of the app.
 */
export default function MobileActionSheet({
  open,
  onClose,
  atmosphereOn,
  onOpenWorld,
  onOpenRelationship,
  onOpenStory,
  onOpenCollections,
  onOpenTimeline,
  onToggleAtmosphere,
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const item = (props) => {
    const { icon: Icon, label, desc, color, testid, onClick } = props;
    return (
      <button
        data-testid={testid}
        onClick={() => { onClick?.(); onClose(); }}
        className="w-full flex items-center gap-3 bg-white border-hard-2 rounded-2xl p-3 text-left press-btn shadow-hard-sm"
      >
        <div className="w-11 h-11 shrink-0 rounded-xl border-hard-2 flex items-center justify-center" style={{ background: color }}>
          <Icon strokeWidth={3} className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-black text-sm leading-tight">{label}</div>
          <div className="text-[11px] text-[#555] leading-tight truncate">{desc}</div>
        </div>
      </button>
    );
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/50"
          onClick={onClose}
          data-testid="mobile-action-sheet"
        >
          <motion.div
            key="sheet"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 26, stiffness: 260 }}
            className="absolute inset-x-0 bottom-0 bg-[#F9F8F6] border-t-[3px] border-[#0A0A0A] rounded-t-3xl p-4 pb-6 max-h-[85vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))" }}
          >
            <div className="w-12 h-1.5 bg-[#0A0A0A] rounded-full mx-auto mb-4" aria-hidden />

            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl">Sohbet Aksiyonları</div>
              <button
                data-testid="mobile-action-close"
                onClick={onClose}
                className="w-9 h-9 border-hard-2 rounded-xl flex items-center justify-center bg-white press-btn shadow-hard-sm"
                aria-label="Kapat"
              >
                <X strokeWidth={3} className="w-4 h-4" />
              </button>
            </div>

            <div className="grid gap-2.5">
              {item({ icon: Sparkles, label: "Sohbet Dünyası", desc: "Tema ve arka planı değiştir", color: "#D4B3FF", testid: "mobile-open-world", onClick: onOpenWorld })}
              {item({ icon: Trophy, label: "Arkadaşlık", desc: "Seviyen ve evcil hayvan", color: "#CCFF00", testid: "mobile-open-relationship", onClick: onOpenRelationship })}
              {item({ icon: BookOpen, label: "Hikaye Modu", desc: "Sohbetten hikaye üret", color: "#FFF7C2", testid: "mobile-open-story", onClick: onOpenStory })}
              {item({ icon: ScrollText, label: "Koleksiyon", desc: "Kaydettiğin anlar", color: "#FFE7CC", testid: "mobile-open-collections", onClick: onOpenCollections })}
              {item({ icon: TrendingUp, label: "Ruh hali zaman çizelgesi", desc: "Sohbetin duygu grafiği", color: "#CEECFF", testid: "mobile-open-timeline", onClick: onOpenTimeline })}
              <button
                data-testid="mobile-toggle-atmosphere"
                onClick={() => { onToggleAtmosphere?.(); onClose(); }}
                className="w-full flex items-center gap-3 bg-white border-hard-2 rounded-2xl p-3 text-left press-btn shadow-hard-sm"
              >
                <div className={`w-11 h-11 shrink-0 rounded-xl border-hard-2 flex items-center justify-center ${atmosphereOn ? "bg-[#CCFF00]" : "bg-white"}`}>
                  <Circle strokeWidth={3} className={`w-5 h-5 ${atmosphereOn ? "fill-[#0A0A0A] text-[#0A0A0A]" : "text-[#555]"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-sm leading-tight">Atmosfer</div>
                  <div className="text-[11px] text-[#555] leading-tight">{atmosphereOn ? "Açık — animasyonlar var" : "Kapalı — sade görünüm"}</div>
                </div>
                <span className={`text-[10px] font-black uppercase tracking-widest border-hard-2 rounded-full px-2 py-0.5 ${atmosphereOn ? "bg-[#CCFF00]" : "bg-white text-[#555]"}`}>
                  {atmosphereOn ? "AÇIK" : "KAPALI"}
                </span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
